import { installSearchI18n } from '@/pocs/deep-docs/features/search/engine'
/**
 * plugins/index.ts
 *
 * Automatically included in `./src/main.ts`
 */

import pinia from '@/stores'
import { createHead } from '@unhead/vue/client'
import { JsonTreeView } from 'json-tree-view-vue3'
import deepUiLib from './deep-ui-lib.ts'
import initializeRouter from './router'
import './deep-libs.ts'
import AppLoader from '@/components/app/AppLoader.vue'
import deviceDetectorJs from './device-detector-js.ts'
import fontawesome from './fontawesome'
import i18n from './i18n'
import sentry from './sentry.ts'
import matomo from './vue-matomo.ts'
import vuetify from './vuetify.ts'
import 'json-tree-view-vue3/style.css'
import { useSettingsDevStore } from '@/stores/settings/settings-dev.ts'
import type { App } from 'vue'

export async function registerPlugins(app: App) {
  app.use(vuetify)
  app.use(pinia)
  app.use(i18n)
  app.use(fontawesome)
  installSearchI18n(i18n)
  app.use(sentry)
  const head = createHead()
  app.use(head)
  app.use(deviceDetectorJs, { refreshOnResize: true })
  app.use(deepUiLib)

  // Global components
  app.component('AppLoader', AppLoader)
  app.component('JsonTreeView', JsonTreeView)

  // Init router after all other plugins
  // https://github.com/dsb-norge/vue-keycloak-js/issues/94#issuecomment-1794403391
  // https://github.com/keycloak/keycloak/issues/14742#issuecomment-1663069438
  const router = initializeRouter()
  app.use(matomo, router)

  // inject route to pinia in order to have access to the router instance inside pinia stores
  // https://stackoverflow.com/questions/70681667/cant-use-vue-router-and-pinia-inside-a-single-store
  pinia.use(({ store }) => {
    store.router = markRaw(router)
  })

  // inject box routes
  // resolve and register layout routes before mounting
  const settingsDevStore = useSettingsDevStore()
  const isDeepDocsLayout = settingsDevStore.canModeFeature('LAYOUT_DEEP_DOCS')
  if (isDeepDocsLayout) {
    const { applyDeepDocsTheme } = await import('../pocs/deep-docs/theme')
    applyDeepDocsTheme(vuetify.theme)
  }
  const { boxRoutes } = isDeepDocsLayout
    ? await import('../pocs/deep-docs/routes/box.ts')
    : await import('./router/routes/box.ts')

  router.addRoute('organization-types-type-boxes-box-layout', boxRoutes)

  // Init router after all other plugins
  // https://github.com/dsb-norge/vue-keycloak-js/issues/94#issuecomment-1794403391
  // https://github.com/keycloak/keycloak/issues/14742#issuecomment-1663069438
  app.use(router)
}
